// ============================================================
// INTERVIEW SCORECARD (merged questions + scoring)
// #3: each question shows its scoring inline + a notes box, so the
//     interviewer scores as they go. Each competency maps to ONE
//     question (no cross-linking glitch).
// #6: loads existing scorecard and UPDATES it (no duplicates).
// ============================================================
import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { ArrowLeft, Check, Lightbulb } from 'lucide-react';
import { api, SCORE_FIELDS, SCORE_LABELS, INTERVIEW_QUESTIONS, SCORE_RUBRIC, RECOMMENDATIONS, fmtSaved } from './constants';

const WEIGHTS = SCORE_FIELDS.reduce((a, f) => ({ ...a, [f.key]: f.weight }), {});
const HINTS = SCORE_FIELDS.reduce((a, f) => ({ ...a, [f.key]: f.hint }), {});
const emptyScores = () => SCORE_FIELDS.reduce((a, f) => ({ ...a, [f.key]: 0 }), {});

export function ScorecardForm({ candidateId, navigate }) {
  const [form, setForm] = useState({ candidate_id: candidateId, recommendation: '', interview_notes: '', question_notes: {}, ...emptyScores() });
  const [existingId, setExistingId] = useState(null);
  const [savedAt, setSavedAt] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    api(`/api/recruitment/candidates/${candidateId}`).then(c => {
      if (!active) return;
      const sc = c.scorecards && c.scorecards[0];
      if (sc) {
        const next = { candidate_id: candidateId, recommendation: sc.recommendation || '', interview_notes: sc.interview_notes || '', question_notes: sc.question_notes || {}, ...emptyScores() };
        SCORE_FIELDS.forEach(f => { next[f.key] = sc[f.key] || 0; });
        setForm(next);
        setExistingId(sc.id);
        setSavedAt(sc.updated_at || sc.created_at);
      }
    }).catch(() => {}).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [candidateId]);

  const setScore = (key, val) => setForm(f => ({ ...f, [key]: val }));
  const setNote = (qkey, text) => setForm(f => ({ ...f, question_notes: { ...(f.question_notes || {}), [qkey]: text } }));

  const totalWeighted = SCORE_FIELDS.reduce((s, f) => s + (form[f.key] || 0) * f.weight, 0);
  const maxWeighted = SCORE_FIELDS.reduce((s, f) => s + 5 * f.weight, 0);
  const pct = maxWeighted > 0 ? Math.round((totalWeighted / maxWeighted) * 100) : 0;
  const scoredCount = SCORE_FIELDS.filter(f => (form[f.key] || 0) > 0).length;
  const interp = pct >= 90 ? 'Strong candidate' : pct >= 75 ? 'Possible candidate' : pct >= 60 ? 'High caution' : 'Do not proceed';
  const interpColor = pct >= 90 ? 'badge-active' : pct >= 75 ? 'badge-improving' : pct >= 60 ? 'badge-draft' : 'badge-critical';

  const save = async () => {
    setSaving(true);
    try {
      const payload = { candidate_id: candidateId, recommendation: form.recommendation, interview_notes: form.interview_notes, question_notes: form.question_notes || {} };
      SCORE_FIELDS.forEach(f => { payload[f.key] = form[f.key] || 0; });
      if (existingId) {
        const res = await api(`/api/recruitment/scorecards/${existingId}`, { method: 'PATCH', body: JSON.stringify(payload) });
        setSavedAt(res.updated_at || new Date().toISOString());
      } else {
        const res = await api('/api/recruitment/scorecards', { method: 'POST', body: JSON.stringify(payload) });
        setExistingId(res.id);
        setSavedAt(res.updated_at || res.created_at || new Date().toISOString());
      }
      toast.success('Scorecard saved');
      navigate('candidate', candidateId);
    } catch { toast.error('Failed to save'); }
    setSaving(false);
  };

  if (loading) return <div className="h-40 bg-muted rounded-2xl animate-skeleton" />;

  return (
    <div data-testid="scorecard-form">
      <button className="ch-back" onClick={() => navigate('candidate', candidateId)}><ArrowLeft size={16} /> Back</button>
      <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
        <h1 className="text-xl font-bold">Interview Scorecard</h1>
        <div className="flex items-center gap-3">
          <span className="text-lg font-bold" data-testid="scorecard-pct">{pct}%</span>
          <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${interpColor}`}>{interp}</span>
        </div>
      </div>
      <p className="text-xs text-muted-foreground mb-1">{scoredCount}/{SCORE_FIELDS.length} competencies scored · read each question, score inline, and jot the applicant's answer.</p>
      {savedAt && <p className="text-xs text-muted-foreground mb-5 flex items-center gap-1"><Check size={12} className="text-[hsl(var(--success))]" /> Last saved {fmtSaved(savedAt)}</p>}
      {!savedAt && <div className="mb-5" />}

      {/* Scoring rubric reference */}
      <div className="ps-card-static p-3 mb-6 flex flex-wrap gap-3">
        {SCORE_RUBRIC.map(r => (
          <span key={r.score} className="flex items-center gap-1.5 text-xs" title={r.desc}>
            <span className="w-5 h-5 rounded bg-muted flex items-center justify-center font-bold text-xs">{r.score}</span>
            <span className="text-muted-foreground">{r.label}</span>
          </span>
        ))}
      </div>

      {/* One card per question: text + guide + inline score + notes */}
      <div className="space-y-3 mb-6">
        {INTERVIEW_QUESTIONS.map((iq, i) => {
          const scored = !!iq.score;
          return (
            <div key={iq.key} className="ps-card-static p-5" data-testid={`question-${iq.key}`}>
              <div className="flex items-start gap-3">
                <span className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold ${scored ? 'bg-primary text-white' : 'bg-[hsl(38_80%_92%)] text-[hsl(38_70%_30%)]'}`}>{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium leading-relaxed">{iq.q}</p>
                  {iq.guide && <p className="text-xs text-muted-foreground mt-1 italic flex items-start gap-1"><Lightbulb size={12} className="mt-0.5 flex-shrink-0" /> {iq.guide}</p>}

                  {scored ? (
                    <div className="flex items-center gap-3 flex-wrap mt-3">
                      <span className="text-xs font-semibold">{SCORE_LABELS[iq.score]} <span className="text-muted-foreground font-normal">(×{WEIGHTS[iq.score]})</span></span>
                      <div className="flex gap-1.5">
                        {[1, 2, 3, 4, 5].map(v => (
                          <button key={v} data-testid={`score-${iq.score}-${v}`} onClick={() => setScore(iq.score, v)}
                            className={'w-9 h-9 rounded-lg text-sm font-bold border transition-colors ' + (form[iq.score] === v ? 'bg-primary text-white border-primary' : 'border-input hover:border-primary')}>{v}</button>
                        ))}
                      </div>
                      <span className="text-xs text-muted-foreground">{(form[iq.score] || 0) * WEIGHTS[iq.score]} pts</span>
                    </div>
                  ) : (
                    <span className="inline-block mt-3 text-[10px] px-2 py-0.5 rounded-full bg-[hsl(38_80%_92%)] text-[hsl(38_70%_30%)] font-semibold uppercase tracking-wide">Insight · not scored</span>
                  )}

                  <textarea value={(form.question_notes && form.question_notes[iq.key]) || ''} onChange={e => setNote(iq.key, e.target.value)}
                    className="rec-input rec-textarea mt-3" rows={2} placeholder="Notes on their answer..." data-testid={`note-${iq.key}`} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Recommendation + overall notes */}
      <div className="ps-card-static p-6">
        <div className="rec-field"><label className="rec-label">Overall Recommendation</label>
          <select value={form.recommendation} onChange={e => setForm(f => ({ ...f, recommendation: e.target.value }))} className="rec-input" data-testid="scorecard-recommendation">
            <option value="">Select...</option>{RECOMMENDATIONS.map(r => <option key={r}>{r}</option>)}
          </select>
        </div>
        <div className="rec-field mt-4"><label className="rec-label">Overall Interview Notes (summary)</label>
          <textarea value={form.interview_notes || ''} onChange={e => setForm(f => ({ ...f, interview_notes: e.target.value }))} className="rec-input rec-textarea" rows={3} data-testid="scorecard-notes" />
        </div>
        <button onClick={save} disabled={saving} className="mt-6 px-6 py-2.5 rounded-xl text-sm font-semibold" style={{ background: 'hsl(var(--primary))', color: 'white' }} data-testid="scorecard-save-btn">{saving ? 'Saving...' : 'Save Scorecard'}</button>
      </div>
    </div>
  );
}
